import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { parseError } from '@/store/modules/helpers/shared'
import apiRentQuarterly from '@/store/api/apiRentQuarterly'
import apiReview from '@/store/api/apiReview'
import { useClientStore } from '@shared/stores/client.js'
import { useClosetStore } from '@shared/stores/closet.js'
import { useCaseStore } from '@/stores/case.js'

export const useQuarterlyRentalsStore = defineStore('quarterlyRentals', () => {
  const clientStore = useClientStore()
  const closetStore = useClosetStore()
  const caseStore = useCaseStore()
  
  const { hasRentalPlan, membershipDetails } = storeToRefs(clientStore)
  const { rentForLongerItems, toReturnItems, rentForLongerItemGroupedByReturnDate } = storeToRefs(closetStore)
  
  const { UPDATE_CLIENT } = clientStore
  const { getPackageItems } = closetStore
  const { setMyCaseFlyoutContext, setMyCaseFlyoutName } = caseStore
  
  const checkoutLoading = ref(false)
  const errors = ref([])
  const itemsToReturn = ref([])
  
  const isClientEligibleForQuarterlyRentals = computed(() => {
    return hasRentalPlan.value && membershipDetails.value?.rentForLongerIneligible === null
  })
  
  const allAvailableItems = computed(() => {
    const rentForLonger = rentForLongerItems.value || []
    const toReturn = toReturnItems.value || []
    const toReturnItemIds = new Set(toReturn.map(item => item.id))
    return rentForLonger.filter(item => !toReturnItemIds.has(item.id))
  })

  const readyToReturnItems = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return allAvailableItems.value.filter(item => {
      if (!item.extensionEligibility) return false
      
      const extension = item.extensionEligibility
      if (extension.eligible === null) {
        return false
      }
      
      const eligibleDate = new Date(extension.eligible)
      eligibleDate.setHours(0, 0, 0, 0)
      return today >= eligibleDate
    })
  })

  const earlyReturnItems = computed(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    return allAvailableItems.value.filter(item => {
      if (!item.extensionEligibility) return false
      
      const extension = item.extensionEligibility
      if (extension.eligible === null) {
        return false
      }
      
      const eligibleDate = new Date(extension.eligible)
      eligibleDate.setHours(0, 0, 0, 0)
      return today < eligibleDate
    })
  })
  const rentForLongerItemsByReturnDate = computed(() => rentForLongerItemGroupedByReturnDate.value)

  const eligibleForRentingQuarterly = computed(() => {
    const rentForLonger = rentForLongerItems.value || []
    if (rentForLonger.length > 0) {
      return true
    }
    return rentForLongerItemsByReturnDate.value && Object.keys(rentForLongerItemsByReturnDate.value).length > 0
  })

  
  const hasQuarterlyItems = computed(() => {
    return rentForLongerItemsByReturnDate.value && Object.keys(rentForLongerItemsByReturnDate.value).length > 0
  })

  // TODO: Remove this after (3/2/26)

  // This is when we will be launching (12/2), 3 month rental period, 
  // and any customers who has items rented for longer before then 
  // will have custom messaging in the Case header.

  const wasSeasonalCapsuleRenter = computed(() => {
    const rentForLonger = rentForLongerItems.value || []
    if (!rentForLonger || !Array.isArray(rentForLonger)) {
      return false
    }
    return rentForLonger.some(packageItem => {
      const returnByDate = new Date(packageItem.item.returnBy)
      const cutoff = new Date('2026-03-02')
      return returnByDate < cutoff
    })
  })
  
  const triggerQuarterlyRentalsFlow = (packageItem) => {
    setMyCaseFlyoutContext({ itemToRent: packageItem })
    setMyCaseFlyoutName('rent-quarterly-checkout')
  }

  const triggerQuarterlyReturnFlow = (packageItem) => {
    itemsToReturn.value = [packageItem]
    setMyCaseFlyoutName(null)
  }

  const setItemsToReturn = (items) => {
    itemsToReturn.value = items
  }

  const clearItemsToReturn = () => {
    itemsToReturn.value = []
  }

  const initializePreselectedItems = () => {
    if (!itemsToReturn.value || !Array.isArray(itemsToReturn.value) || itemsToReturn.value.length === 0) {
      return []
    }
    
    if (!allAvailableItems.value || allAvailableItems.value.length === 0) {
      return []
    }
    
    const preselectedIds = new Set(itemsToReturn.value.map(item => item.id))
    return allAvailableItems.value.filter(item => preselectedIds.has(item.id))
  }


  const isItemEligibleForQuarterlyRental = (packageItem) => {
    if (packageItem.extensionEligibility) {
      const extension = packageItem.extensionEligibility
      
      if (extension.eligible === null) {
        return false
      }
      
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const eligibleDate = new Date(extension.eligible)
      eligibleDate.setHours(0, 0, 0, 0)
      
      return today >= eligibleDate
    }
  
    return packageItem.canRentForLonger?.eligible ?? false
  }

  const isEarlyReturnItem = (packageItem) => {
    return earlyReturnItems.value.some(item => item.id === packageItem.id)
  }

  const rentItemQuarterly = async (itemToRentId) => {
    errors.value = []
    try {
      await apiRentQuarterly.rentItemQuarterly(itemToRentId)
      await getPackageItems()
      setMyCaseFlyoutName('rent-quarterly-complete')
    } catch (error) {
      errors.value = parseError(error)
      throw error
    }
  }

  const getRentForLongerPreview = async (itemToRentId, promoCode = null) => {
    const result = await apiRentQuarterly.getRentForLongerPreview(itemToRentId, promoCode)
    return result
  }

  const returnItems = async () => {
    checkoutLoading.value = true
    errors.value = []
    try {
      const itemIds = itemsToReturn.value.map(item => item.id)
      const res = await apiReview.swapItems(itemIds)
      if (res.data) {
        UPDATE_CLIENT(res.data)
        await getPackageItems()
      }
      return true
    } catch (error) {
      errors.value = parseError(error)
      throw error
    } finally {
      checkoutLoading.value = false
    }
  }

  return {
    triggerQuarterlyRentalsFlow,
    triggerQuarterlyReturnFlow,
    isClientEligibleForQuarterlyRentals,
    rentItemQuarterly,
    getRentForLongerPreview,
    returnItems,
    setItemsToReturn,
    clearItemsToReturn,
    initializePreselectedItems,
    isItemEligibleForQuarterlyRental,
    isEarlyReturnItem,
    checkoutLoading,
    errors,
    itemsToReturn,
    allAvailableItems,
    readyToReturnItems,
    earlyReturnItems,
    rentForLongerItemsByReturnDate,
    eligibleForRentingQuarterly,
    hasQuarterlyItems,
    wasSeasonalCapsuleRenter
  }
})